import {
  useMediaQuery,
  WhmButton,
  WhmFlexDataGridView,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { InfoCard } from "../../shared/InfoCard";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { useContext } from "react";
import useUserInfo from "../../../../hooks/useUserInfo";
import { iconSmall } from "../../../shared/iconThemes";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import StyledCard from "../../../Portfolio/components/StyledCard";
import { showsAsEnhanced } from "./utils";

export default function KeyPartiesSkeleton() {
  const user = useUserInfo();
  const enhanced = showsAsEnhanced(user);
  const { getTranslation } = useContext(I18nContext);
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  return (
    <WhmStack gap="16px">
      <InfoCard
        title={
          isMobile ? (
            <WhmSkeleton width="100%" height="108px" variant="rectangular" />
          ) : (
            <WhmSkeleton width="100%" height="48px" variant="rectangular" />
          )
        }
      >
        <WhmFlexDataGridView
          disableColumnResize
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{ pageSize: 10, page: 0 }}
          rows={new Array(5).fill(0).map((_, index) => ({ id: index }))}
          sx={{
            "& .MuiDataGrid-virtualScrollerContent": { minHeight: "unset" },
            "--DataGrid-overlayHeight": "300px",
            minHeight: "500px",
            border: "none",
          }}
          slotProps={{
            pagination: {
              slotProps: {
                select: { sx: { minWidth: "unset" } },
              },
            },
          }}
          CardComponent={() => (
            <StyledCard>
              <WhmStack
                justifyContent="space-between"
                gap="16px"
                direction="row"
                alignItems="center"
              >
                <WhmSkeleton variant="rounded" height="40px" sx={{ flex: 1 }} />
                <WhmSkeleton variant="rounded" height="32px" width="32px" />
              </WhmStack>
            </StyledCard>
          )}
          columns={[
            {
              field: "name",
              flex: 1,
              headerName: getTranslation(
                translationKeys.PROFILE_KEY_PARTIES_MEMBER_NAME_COLUMN,
              ),
              renderCell: () => (
                <WhmStack height="100%" direction="row" alignItems="center">
                  <WhmSkeleton
                    width="100%"
                    height="20px"
                    sx={{ transform: "scale(1)" }}
                  />
                </WhmStack>
              ),
              disableColumnMenu: true,
              filterable: false,
              editable: false,
              hideable: false,
            },
            {
              field: "ownership",
              flex: 1,
              renderHeader: () => (
                <WhmStack
                  direction="row"
                  alignItems="center"
                  gap="4px"
                  height="100%"
                >
                  {enhanced ? (
                    <WhmStack direction="row" alignItems="center" gap="4px">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_OWNERSHIP_COLUMN
                        }
                      />
                      <WhmTooltip
                        title={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_PERCENT_OWNERSHIP_COLUMN_TOOLTIP
                            }
                          />
                        }
                      >
                        <InfoOutlined style={iconSmall} />
                      </WhmTooltip>
                    </WhmStack>
                  ) : (
                    <WhmStack direction="row" alignItems="center" gap="4px">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_OWNERSHIP_COLUMN
                        }
                      />
                      <WhmTooltip
                        title={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_PERCENT_OWNERSHIP_COLUMN_TOOLTIP
                            }
                          />
                        }
                      >
                        <InfoOutlined style={iconSmall} />
                      </WhmTooltip>
                    </WhmStack>
                  )}
                </WhmStack>
              ),
              renderCell: () => (
                <WhmStack height="100%" direction="row" alignItems="center">
                  <WhmSkeleton
                    width="100%"
                    height="20px"
                    sx={{ transform: "scale(1)" }}
                  />
                </WhmStack>
              ),
              disableColumnMenu: true,
              filterable: false,
              editable: false,
              hideable: false,
            },
            {
              field: "legalSigner",
              flex: 1,
              renderHeader: () => (
                <WhmStack
                  direction="row"
                  alignItems="center"
                  gap="4px"
                  height="100%"
                >
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_COLUMN
                    }
                  />
                  <WhmTooltip
                    title={
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_COLUMN_TOOLTIP
                        }
                      />
                    }
                  >
                    <InfoOutlined style={iconSmall} />
                  </WhmTooltip>
                </WhmStack>
              ),
              renderCell: () => (
                <WhmStack height="100%" direction="row" alignItems="center">
                  <WhmSkeleton
                    width="100%"
                    height="20px"
                    sx={{ transform: "scale(1)" }}
                  />
                </WhmStack>
              ),
              disableColumnMenu: true,
              filterable: false,
              editable: false,
              hideable: false,
            },
            {
              field: "actions",
              flex: 1,
              hideSortIcons: true,
              headerName: "",
              renderCell: () => (
                <WhmStack height="100%" direction="row" alignItems="center">
                  <WhmSkeleton
                    width="100%"
                    height="20px"
                    sx={{ transform: "scale(1)" }}
                  />
                </WhmStack>
              ),
              disableColumnMenu: true,
              filterable: false,
              sortable: false,
              editable: false,
              hideable: false,
            },
          ]}
        />
      </InfoCard>
      <WhmButton
        sx={{ width: { xs: "100%", xl: "fit-content" } }}
        variant="contained"
      >
        <Translate translationKey={translationKeys.PROFILE_KEY_PARTIES_SAVE} />
      </WhmButton>
      <WhmTypography variant="body2" color="text.secondary">
        <TranslateHtml
          translationKey={translationKeys.PROFILE_KEY_PARTIES_SAVE_NOTE}
        />
      </WhmTypography>
    </WhmStack>
  );
}
